<template>
  <BaseCard
    :result="{
      orgName: searchResult.secondary_organization.name,
      orgLink: searchResult.link,
      tagName: $t(`search.events.tagName`),
      tagLink: `${searchResult.link}/events`,
    }"
    class="event-card result-card"
  >
    <template #preview>
      <div class="preview-fallback">
        <div class="day">{{ getShortDay }}</div>
        <div class="month">{{ getShortMonth }}</div>
      </div>
    </template>

    <template #result-content>
      <a
        :href="`${searchResult.link}/events?=${searchResult.id}`"
        class="title"
        >{{ searchResult.title }}</a
      >
      <div
        v-if="searchResult.description"
        class="snippet"
      >
        <span v-html="searchResult.description"></span>
      </div>

      <div
        class="meta-info"
        :class="{ 'is-mobile-view': isMobileView }"
      >
        <div class="date">
          <Icon
            name="lucide:calendar"
            size="14"
          />
          {{ formattedEventDateAndTime }}
        </div>
        <a
          v-if="searchResult.venue"
          :href="`http://maps.google.com/?q=${encodeURIComponent(searchResult.venue)}`"
          class="location"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon
            name="lucide:map-pin"
            size="14"
          />
          {{ searchResult.venue }}
        </a>
      </div>

      <div
        v-if="!isMobileView"
        class="card-buttons"
      >
        <button
          class="button add-to-calendar"
          :aria-expanded="showCalendarButtons.toString()"
          :aria-controls="`add-buttons-${searchResult.id}`"
          @click="toggleCalendarButtons"
        >
          <Icon
            name="lucide:calendar-plus"
            size="14"
          />
          {{ $t('search.events.addToCalendar') }}
        </button>
        <!-- <button class="button register">
          <Icon name="lucide:ticket" size="14" />
          {{ $t('search.events.register') }}
        </button> -->
        <div
          v-show="showCalendarButtons"
          :id="`add-buttons-${searchResult.id}`"
          class="calendar-popup"
          :class="{ 'is-visible': showCalendarButtons }"
        >
          <EventListButtons
            :show-calendar-buttons="showCalendarButtons"
            :event-data="searchResult"
            :handle-back-button-click="handleBackButtonClick"
            :handle-blur="handleBlur"
            use-close-icon
          />
        </div>
        <div
          v-if="searchResult.publish_date"
          class="dates-and-times"
        >
          <div class="published-at">{{ $t('search.forms.published') }}: {{ formattedPublishedDateAndTime }}</div>
          <div class="updated-at"></div>
        </div>
      </div>
    </template>
  </BaseCard>
</template>

<script setup>
import BaseCard from './BaseCard.vue';
import { computed, ref } from 'vue';
import { monthDayYear } from '../../../helpers/date-utils';
import { useScreenBreakpoints } from '@/composables/useBreakpoints';
import EventListButtons from '@/components/events/components/EventViews/List/EventListButtons.vue';

const { isMobileView } = useScreenBreakpoints();

const props = defineProps({
  searchResult: {
    type: Object,
    required: true,
  },
});

const formattedPublishedDateAndTime = computed(() => {
  return monthDayYear(props.searchResult.publish_date) || '';
});

const formattedEventDateAndTime = computed(() => {
  const formattedDate = props.searchResult.formatted_date;
  const lastFiveCharactersInDate = formattedDate.slice(formattedDate.length - 5, formattedDate.length);
  const allDayEventsWithNoTime = lastFiveCharactersInDate === ' from';
  if (allDayEventsWithNoTime) {
    return formattedDate.slice(0, -5);
  }
  return formattedDate;
});

const getShortMonth = computed(() => {
  const date = new Date(props.searchResult.event_date);
  return date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
});

const getShortDay = computed(() => {
  const date = new Date(props.searchResult.event_date);
  return date.getDate().toString();
});

const showCalendarButtons = ref(false);
function toggleCalendarButtons() {
  showCalendarButtons.value = !showCalendarButtons.value;
}
function handleBackButtonClick() {
  showCalendarButtons.value = false;
}
function handleBlur() {
  showCalendarButtons.value = false;
}
</script>

<style scoped>
.event-card {
  --color-grey-50: #f7f7f7;
  --color-grey-700: #616161;
  --color-grey-950: #2d2d2d;
  --background-color: var(--color-grey-50);
  --text-color: var(--color-grey-950);

  .title {
    line-height: 120%;
  }

  .snippet {
    line-height: 19px;
  }

  .day {
    font-size: 1.5rem;
    font-weight: bold;
  }

  .month {
    font-size: 1rem;
    color: var(--color-grey-700);
    text-transform: uppercase;
  }
  .meta-info.is-mobile-view {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .card-buttons {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    .button {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 0.5rem 1rem;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      border-radius: 20px;
      border: 1px solid var(--color-grey-100);
      background: var(--primary-white);
      color: var(--color-purple);
      transition:
        background 0.2s,
        color 0.2s;
      &:hover,
      &:focus-visible {
        background: var(--color-purple);
        color: var(--primary-white);
      }
      &.add-to-calendar {
        background-color: var(--secondary-button-background);
        color: var(--secondary-button-text);
        border: var(--secondary-button-border);
        border-radius: var(--secondary-button-border-radius);
        &:hover {
          background-color: var(--secondary-button-hover);
        }
        &:focus-visible {
          outline: var(--secondary-button-focus-outline);
          outline-offset: 1px;
          border: var(--secondary-button-hover-border);
        }
      }
      svg {
        margin-right: 6px;
      }
    }
    .calendar-popup {
      position: absolute;
      z-index: 10;
      margin-top: 44px;
      background: var(--primary-white, #fff);
      border: 1px solid var(--color-grey-100, #e0e0e0);
      border-radius: 12px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      padding: 12px 0 8px 0;
      min-width: 180px;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 4px;
      outline: none;
      transition:
        opacity 0.18s ease,
        transform 0.18s ease;
      opacity: 0;
      pointer-events: none;
      color: var(--color-grey-950, #2d2d2d);
    }
    .calendar-popup.is-visible {
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0);
      animation: fadeInPopup 0.18s;
    }
    .calendar-popup:focus-visible {
      box-shadow:
        0 0 0 2px var(--color-purple),
        0 2px 8px rgba(0, 0, 0, 0.08);
    }
    @keyframes fadeInPopup {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
}
</style>
